import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  X, Camera, ImageIcon, RotateCcw, Sparkles, Loader2, Check, PencilLine, AlertTriangle,
} from "lucide-react";

const LOADING_MSGS = [
  "Identifying your food…",
  "Estimating portions…",
  "Calculating nutrition…",
  "Preparing your insights…",
];
const MEALS = [
  { v: "breakfast", label: "Breakfast" },
  { v: "lunch", label: "Lunch" },
  { v: "dinner", label: "Dinner" },
  { v: "snack", label: "Snack" },
];

export default function Scan() {
  const navigate = useNavigate();
  const [image, setImage] = useState(null); // dataURL
  const [phase, setPhase] = useState("capture"); // capture | preview | analyzing | result | error | manual
  const [result, setResult] = useState(null);
  const [mealType, setMealType] = useState("lunch");
  const [msgIdx, setMsgIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [manual, setManual] = useState({ meal_name: "", calories: "", protein: "", carbs: "", fat: "" });

  useEffect(() => {
    if (phase !== "analyzing") return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length), 1600);
    return () => clearInterval(t);
  }, [phase]);

  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
      setPhase("preview");
    };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    setPhase("analyzing");
    setMsgIdx(0);
    try {
      const r = await api.scan(image);
      if (!r.success) {
        setPhase("error");
        return;
      }
      setResult(r);
      setPhase("result");
    } catch (e) {
      setPhase("error");
    }
  };

  const addMeal = async () => {
    setSaving(true);
    try {
      const t = result.total || {};
      await api.addMeal({
        meal_type: mealType,
        meal_name: result.meal_name || "Scanned meal",
        image_url: result.image_url || image,
        calories: t.calories || 0,
        protein: t.protein || 0,
        carbs: t.carbs || 0,
        fat: t.fat || 0,
        fiber: t.fiber || 0,
        items: result.items || [],
        ai_insight: result.insight || "",
      });
      toast.success("Meal added to your day!");
      navigate("/");
    } catch (e) {
      toast.error(e.message || "Could not add meal");
    } finally {
      setSaving(false);
    }
  };

  const addManual = async () => {
    if (!manual.meal_name || !manual.calories) {
      toast.error("Add at least a name and calories");
      return;
    }
    setSaving(true);
    try {
      await api.addMeal({
        meal_type: mealType,
        meal_name: manual.meal_name,
        image_url: image,
        calories: Number(manual.calories) || 0,
        protein: Number(manual.protein) || 0,
        carbs: Number(manual.carbs) || 0,
        fat: Number(manual.fat) || 0,
        items: [],
      });
      toast.success("Meal added!");
      navigate("/");
    } catch (e) {
      toast.error(e.message || "Could not add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-neutral-950 text-white flex flex-col">
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={pick} className="hidden" data-testid="camera-input" />
      <input ref={galRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="gallery-input" />

      {/* top bar */}
      <div className="flex items-center justify-between px-5 pt-6 pb-3">
        <button onClick={() => navigate("/")} data-testid="scan-close" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
        <span className="font-display text-lg font-medium">Scan your meal</span>
        <span className="w-10" />
      </div>

      {/* body */}
      <div className="flex-1 relative overflow-hidden">
        {(phase === "capture") && (
          <div className="h-full flex flex-col items-center justify-center px-8 text-center gap-6">
            <div className="relative w-64 h-64">
              <Corners />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
                <Camera className="w-12 h-12" />
                <p className="text-sm px-6">Take a photo and let AI estimate what's on your plate.</p>
              </div>
            </div>
          </div>
        )}

        {(phase === "preview" || phase === "analyzing" || phase === "error") && image && (
          <div className="h-full relative">
            <img src={image} alt="meal" className="w-full h-full object-cover opacity-90" />
            {phase === "preview" && <div className="absolute inset-0 pointer-events-none"><div className="absolute inset-8"><Corners /></div></div>}

            {phase === "analyzing" && (
              <div className="absolute inset-0 bg-neutral-950/70 backdrop-blur-sm flex flex-col items-center justify-center gap-5">
                <div className="relative w-20 h-20">
                  <div className="absolute inset-0 rounded-full border-2 border-white/20" />
                  <div className="absolute inset-0 rounded-full border-2 border-t-white border-transparent animate-spin" />
                  <Sparkles className="absolute inset-0 m-auto w-7 h-7" />
                </div>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={msgIdx}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="text-sm text-white/90 font-medium"
                    data-testid="analyzing-msg"
                  >
                    {LOADING_MSGS[msgIdx]}
                  </motion.p>
                </AnimatePresence>
              </div>
            )}

            {phase === "error" && (
              <div className="absolute inset-0 bg-neutral-950/80 flex flex-col items-center justify-center gap-4 px-8 text-center">
                <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-accent" />
                </div>
                <p className="text-white/90">We couldn't confidently identify this meal.</p>
                <div className="flex flex-col gap-2 w-full max-w-xs">
                  <Button onClick={analyze} data-testid="retry-btn" className="rounded-full bg-white text-neutral-950 hover:bg-white/90">Try again</Button>
                  <Button onClick={() => setPhase("manual")} data-testid="manual-btn" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10">
                    <PencilLine className="w-4 h-4" /> Enter food manually
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* bottom controls */}
      {phase === "capture" && (
        <div className="px-8 pb-10 pt-4 flex items-center justify-around">
          <button onClick={() => galRef.current?.click()} data-testid="open-gallery" className="flex flex-col items-center gap-1.5 text-white/80">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><ImageIcon className="w-5 h-5" /></div>
            <span className="text-xs">Gallery</span>
          </button>
          <button onClick={() => camRef.current?.click()} data-testid="open-camera" className="w-18 h-18 w-[72px] h-[72px] rounded-full bg-white flex items-center justify-center active:scale-95 transition-transform">
            <div className="w-14 h-14 rounded-full border-4 border-neutral-950 flex items-center justify-center">
              <Camera className="w-6 h-6 text-neutral-950" />
            </div>
          </button>
          <button onClick={() => setPhase("manual")} data-testid="manual-entry-open" className="flex flex-col items-center gap-1.5 text-white/80">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><PencilLine className="w-5 h-5" /></div>
            <span className="text-xs">Manual</span>
          </button>
        </div>
      )}

      {phase === "preview" && (
        <div className="px-6 pb-10 pt-4 flex gap-3">
          <Button onClick={() => { setImage(null); setPhase("capture"); }} data-testid="retake-btn" variant="outline" className="flex-1 rounded-full py-6 border-white/30 bg-transparent text-white hover:bg-white/10">
            <RotateCcw className="w-4 h-4" /> Retake
          </Button>
          <Button onClick={analyze} data-testid="analyze-btn" className="flex-[2] rounded-full py-6 bg-primary hover:bg-primary/90 text-white">
            <Sparkles className="w-4 h-4" /> Analyze food
          </Button>
        </div>
      )}

      {/* result bottom sheet */}
      <AnimatePresence>
        {phase === "result" && result && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute inset-x-0 bottom-0 max-h-[85%] rounded-t-3xl bg-white/90 backdrop-blur-xl text-foreground overflow-y-auto no-scrollbar"
            data-testid="scan-result"
          >
            <div className="p-6 space-y-5">
              <div className="w-10 h-1 rounded-full bg-border mx-auto" />
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl font-medium tracking-tight">{result.meal_name}</h3>
                <span className={`text-xs px-2.5 py-1 rounded-full ${result.confidence === "high" ? "bg-primary/10 text-primary" : "bg-macro-carbs/10 text-macro-carbs"}`}>
                  {result.confidence} confidence
                </span>
              </div>

              <div className="space-y-2">
                {(result.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-2">
                    <span className="text-foreground/80">{it.name} <span className="text-muted-foreground">· {it.portion}</span></span>
                    <span className="font-semibold tabular-nums">{Math.round(it.calories)} kcal</span>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-primary text-primary-foreground p-4 flex items-center justify-between">
                <span className="text-sm opacity-90">Total</span>
                <span className="text-2xl font-display font-medium tabular-nums">{Math.round((result.total || {}).calories || 0)} kcal</span>
              </div>

              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Protein", result.total?.protein, "#4A5D23"], ["Carbs", result.total?.carbs, "#E69C24"], ["Fat", result.total?.fat, "#C45A39"], ["Fiber", result.total?.fiber, "#8B7355"]].map(([l, v, c]) => (
                  <div key={l} className="rounded-xl bg-secondary p-2.5">
                    <div className="text-base font-display font-medium tabular-nums" style={{ color: c }}>{Math.round(v || 0)}g</div>
                    <div className="text-[10px] text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>

              {result.insight && (
                <div className="rounded-2xl bg-accent/8 border border-accent/20 p-4">
                  <div className="flex items-center gap-1.5 text-accent font-semibold text-sm mb-1"><Sparkles className="w-4 h-4" /> AI Insight</div>
                  <p className="text-sm text-foreground/80 leading-relaxed">{result.insight}</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground text-center">AI estimates may not be exact.</p>

              <div>
                <Label className="text-sm font-semibold">Add to</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {MEALS.map((m) => (
                    <button
                      key={m.v}
                      data-testid={`mealtype-${m.v}`}
                      onClick={() => setMealType(m.v)}
                      className={`py-2 rounded-xl text-xs font-semibold border transition-colors ${mealType === m.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={addMeal} disabled={saving} data-testid="add-meal-btn" className="w-full rounded-full py-6 text-base font-semibold bg-primary hover:bg-primary/90">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Add to today's meals
              </Button>
            </div>
          </motion.div>
        )}

        {phase === "manual" && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute inset-x-0 bottom-0 max-h-[90%] rounded-t-3xl bg-white text-foreground overflow-y-auto no-scrollbar"
            data-testid="manual-sheet"
          >
            <div className="p-6 space-y-4">
              <div className="w-10 h-1 rounded-full bg-border mx-auto" />
              <h3 className="font-display text-2xl font-medium tracking-tight">Enter food manually</h3>
              <div className="space-y-3">
                <ManualField label="Food name" value={manual.meal_name} onChange={(v) => setManual({ ...manual, meal_name: v })} placeholder="e.g. Chicken salad" testid="m-name" />
                <div className="grid grid-cols-2 gap-3">
                  <ManualField label="Calories" value={manual.calories} onChange={(v) => setManual({ ...manual, calories: v })} type="number" testid="m-cal" />
                  <ManualField label="Protein (g)" value={manual.protein} onChange={(v) => setManual({ ...manual, protein: v })} type="number" testid="m-pro" />
                  <ManualField label="Carbs (g)" value={manual.carbs} onChange={(v) => setManual({ ...manual, carbs: v })} type="number" testid="m-carb" />
                  <ManualField label="Fat (g)" value={manual.fat} onChange={(v) => setManual({ ...manual, fat: v })} type="number" testid="m-fat" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Add to</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {MEALS.map((m) => (
                      <button key={m.v} onClick={() => setMealType(m.v)} className={`py-2 rounded-xl text-xs font-semibold border ${mealType === m.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}>{m.label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setPhase(image ? "error" : "capture")} variant="outline" className="flex-1 rounded-full py-6">Cancel</Button>
                <Button onClick={addManual} disabled={saving} data-testid="save-manual-btn" className="flex-[2] rounded-full py-6 bg-primary hover:bg-primary/90">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Add meal
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Corners() {
  const c = "absolute w-8 h-8 border-white/70";
  return (
    <>
      <div className={`${c} top-0 left-0 border-t-2 border-l-2 rounded-tl-2xl`} />
      <div className={`${c} top-0 right-0 border-t-2 border-r-2 rounded-tr-2xl`} />
      <div className={`${c} bottom-0 left-0 border-b-2 border-l-2 rounded-bl-2xl`} />
      <div className={`${c} bottom-0 right-0 border-b-2 border-r-2 rounded-br-2xl`} />
    </>
  );
}

function ManualField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold text-foreground/70">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-12 rounded-xl bg-secondary/50" />
    </div>
  );
}
